import React, { Key, ReactElement, useContext, useMemo } from 'react'
// Commons imports.
import { ExpansionPanelItem } from '../../Common/Components/ExpansionPanel'
import BulkInsertExpansionPanel from '../BulkInsertExpansionPanel'
// Contexts imports.
import { LanguageContext } from '../../Common/Contexts'
// Utilities imports.
import { LanguageBulkInsertUsers, LanguageErrorCodes } from '../../../../i18n'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { GeneralErrorGeneric, User, ValidationErrorCSV } from '../../Common/Types/Types'
import { DataTable, DataTableColumn } from '../../../ReactRouting/Components/Molecules/DataTable/DataTable'
import { AdminBackendPermissionNames } from '../../Common/Constants/Permissions'
import { ArrayUtils } from '../../../NonReact/utils/ArrayUtils'
import { IsVarEmpty } from '../../../NonReact/utils/GlobalFunctions'
// CSS imports. Commented out because of current build process.
// import './validationStep.scss';

const USER_TABLE_ROW_CLASS = 'user-table-row'
const USER_TABLE_CELL_CLASS = 'user-table-cell'
const USER_TABLE_HEADER_CLASS = 'user-table-header'
const USER_LIST_CLASS = 'user-list'

/**
 * Row model for routed {@link DataTable} preview of validated users (requires {@link DataTable} shape constraints).
 */
type BulkInsertUserPreviewTableRow = {
    key: Key
    Description: string
    UserID: string
    EmailAddress: string
    PermissionsReportingAccess: string
    PermissionsUserSystemAdministrator: string
    PermissionsUserMobileAppAdvanced: string
}

/**
 * Component properties type.
 */
type Props = {
    generalError: OnlyNullable<GeneralErrorGeneric>
    errors: Map<number, ValidationErrorCSV[]>
    users: User[]
    /**
     * When true, success preview uses the routed data table instead of a legacy HTML table.
     */
    isRouterMode?: boolean
}

/**
 * Bulk upload step for file import.
 *
 * @param generalError General error besides the validation.
 * @param errors Validation errors.
 * @param users Users to be configured.
 * @param isRouterMode When true, preview list uses routed data table styling.
 */
const ValidationStep: React.FC<Props> = ({
    generalError,
    errors,
    users,
    isRouterMode = false
}: Props): ReactElement => {
    const language = useContext(LanguageContext) as CLanguageHandler
    const isValidationSuccessful = Array.from(errors).length === 0 && IsVarEmpty(generalError)

    if (!isValidationSuccessful) {
        language.loadBundle(LanguageErrorCodes.NAME)
    }

    /**
     * Maps error code to message.
     * @param errorCode the error code.
     */
    const getErrorCodeMessage = (errorCode: number): string => {
        const errorCodeKey = (LanguageErrorCodes as unknown as Record<string, string>)[`Errorcode${errorCode}`]

        return language.get(errorCodeKey)
    }

    // Define expansion panel error items from errors.
    const expansionPanelErrorItems: ExpansionPanelItem[] = Array.from(errors).map(([key, value]) => ({
        title: <div className='expansion-panel-header'>{`Line No: ${key}`}</div>,
        content: (
            <div>
                {value.map((error: ValidationErrorCSV, index: number) => (
                    <div key={`${error.ErrorCode}${index}`} className='cErrorContent'>
                        <span>Error code {error.ErrorCode}:</span>
                        {error.HasPlaceholder ? (
                            <span>{` ${error.ErrorMessage}`}</span>
                        ) : (
                            <span>{` ${getErrorCodeMessage(error.ErrorCode)}`}</span>
                        )}
                    </div>
                ))}
            </div>
        )
    }))

    const userPreviewColumns: DataTableColumn<BulkInsertUserPreviewTableRow, keyof BulkInsertUserPreviewTableRow>[] =
        useMemo(
            (): DataTableColumn<BulkInsertUserPreviewTableRow, keyof BulkInsertUserPreviewTableRow>[] => [
                {
                    headerName: language.get(LanguageBulkInsertUsers.Resulttableheaderusername),
                    field: 'UserID',
                    key: 'bulk-insert-preview-userid',
                    sortable: true,
                    align: 'left'
                },
                {
                    headerName: language.get(LanguageBulkInsertUsers.Resulttableheaderemail),
                    field: 'EmailAddress',
                    key: 'bulk-insert-preview-email',
                    sortable: true,
                    align: 'left'
                },
                {
                    headerName: language.get(LanguageBulkInsertUsers.Resulttableheaderrptaccess),
                    field: 'PermissionsReportingAccess',
                    key: 'bulk-insert-preview-rpt',
                    sortable: true,
                    align: 'left'
                },
                {
                    headerName: language.get(LanguageBulkInsertUsers.Resulttableheaderisadmin),
                    field: 'PermissionsUserSystemAdministrator',
                    key: 'bulk-insert-preview-admin',
                    sortable: true,
                    align: 'left'
                },
                {
                    headerName: language.get(LanguageBulkInsertUsers.Resulttableheaderenhancemobileapp),
                    field: 'PermissionsUserMobileAppAdvanced',
                    key: 'bulk-insert-preview-mobile',
                    sortable: true,
                    align: 'left'
                }
            ],
            [language]
        )

    const userPreviewRows: BulkInsertUserPreviewTableRow[] = useMemo(
        (): BulkInsertUserPreviewTableRow[] =>
            users.map(
                (user: User, index: number): BulkInsertUserPreviewTableRow => ({
                    key: `${user.UserID}-${String(index)}`,
                    Description: user.UserID,
                    UserID: user.UserID,
                    EmailAddress: user.EmailAddress,
                    PermissionsReportingAccess: user.PermissionsReportingAccess,
                    PermissionsUserSystemAdministrator: String(user.PermissionsUserSystemAdministrator),
                    PermissionsUserMobileAppAdvanced: String(user.PermissionsUserMobileAppAdvanced)
                })
            ),
        [users]
    )

    /**
     * Builds users table element (legacy non-routed layout).
     */
    const getUsersTable = (): ReactElement => {
        const expansionPanelSuccessItems: ReactElement[] = users.map(
            (user: User): ReactElement => (
                <tr key={String(user.UserID)} className={USER_TABLE_ROW_CLASS}>
                    <td className={USER_TABLE_CELL_CLASS}>{user.UserID}</td>
                    <td className={USER_TABLE_CELL_CLASS}>{user.EmailAddress}</td>
                    <td className={USER_TABLE_CELL_CLASS}>{user.PermissionsReportingAccess}</td>
                    <td className={USER_TABLE_CELL_CLASS}>{String(user.PermissionsUserSystemAdministrator)}</td>
                    <td className={USER_TABLE_CELL_CLASS}>{String(user.PermissionsUserMobileAppAdvanced)}</td>
                </tr>
            )
        )

        return (
            <table className='user-table'>
                <thead>
                    <tr>
                        <th className={USER_TABLE_HEADER_CLASS}>
                            {language.get(LanguageBulkInsertUsers.Resulttableheaderusername)}
                        </th>
                        <th className={USER_TABLE_HEADER_CLASS}>
                            {language.get(LanguageBulkInsertUsers.Resulttableheaderemail)}
                        </th>
                        <th className={USER_TABLE_HEADER_CLASS}>
                            {language.get(LanguageBulkInsertUsers.Resulttableheaderrptaccess)}
                        </th>
                        <th className={USER_TABLE_HEADER_CLASS}>
                            {language.get(LanguageBulkInsertUsers.Resulttableheaderisadmin)}
                        </th>
                        <th className={USER_TABLE_HEADER_CLASS}>
                            {language.get(LanguageBulkInsertUsers.Resulttableheaderenhancemobileapp)}
                        </th>
                    </tr>
                </thead>
                <tbody>{expansionPanelSuccessItems}</tbody>
            </table>
        )
    }

    /**
     * Routed data-table preview of validated users.
     */
    const getUsersDataTable = (): ReactElement => (
        <div className='bulk-csv-wizard-datatable'>
            <DataTable<BulkInsertUserPreviewTableRow, keyof BulkInsertUserPreviewTableRow>
                permissionName={AdminBackendPermissionNames.USER}
                toolbarDisabled
                columns={userPreviewColumns}
                data={userPreviewRows}
            />
        </div>
    )

    const renderUserPreview = (): ReactElement => (isRouterMode ? getUsersDataTable() : getUsersTable())

    // Define general error items.
    const generalErrorItems = (): ReactElement => (
        <p key={`${generalError?.ErrorCode}`} className='align-center'>
            Error: {generalError ? generalError?.ErrorMessage : ''}
        </p>
    )

    return (
        <div className='validation-step'>
            {!isValidationSuccessful ? (
                <React.Fragment>
                    {generalError ? (
                        <React.Fragment>
                            <div className='text errors-title'>{generalErrorItems()}</div>
                            {!ArrayUtils.isEmpty(Array.from(users)) && (
                                <div className={USER_LIST_CLASS}>{renderUserPreview()}</div>
                            )}
                        </React.Fragment>
                    ) : (
                        <React.Fragment>
                            <div className='text errors-title'>
                                {language.get(LanguageBulkInsertUsers.LanguageBulkInsertUsers_ValidationError)}
                            </div>
                            <BulkInsertExpansionPanel expansionPanelItems={expansionPanelErrorItems} />
                        </React.Fragment>
                    )}
                </React.Fragment>
            ) : (
                <React.Fragment>
                    <div className='text success-title'>
                        {language.get(LanguageBulkInsertUsers.LanguageBulkInsertUsers_SuccessTitle)}
                    </div>
                    <div className={USER_LIST_CLASS}>{renderUserPreview()}</div>
                </React.Fragment>
            )}
        </div>
    )
}

export default ValidationStep
